import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import {
  MAX_RECEIPT_BYTES,
  ReceiptTooLargeError,
} from '../receipts/prepareUpload.js';
import type { SyncState } from './jobsDb.js';

export const PHOTO_LOST_REASON = 'Photo lost — re-take';

export type ReceiptStatus =
  | 'pending_upload'
  | 'pending_extraction'
  | 'awaiting_review'
  | 'confirmed'
  | 'sent_to_accounts';

export type LocalReceipt = {
  id: string;
  status: ReceiptStatus;
  captured_at: string;
  mime_type: string;
  /** Local image blob until server confirms upload (cleared only then). */
  blob: Blob | null;
  /** Object URL for offline preview — revoked on replace. */
  localPreviewUrl: string | null;
  supplier_name: string | null;
  receipt_date: string | null;
  currency: string | null;
  net_pence: number | null;
  vat_pence: number | null;
  gross_pence: number | null;
  gbp_net_pence?: number | null;
  gbp_vat_pence?: number | null;
  gbp_gross_pence?: number | null;
  fx_rate?: number | null;
  fx_rate_as_of?: string | null;
  fx_source?: string | null;
  fx_period?: string | null;
  category: string | null;
  supplier_vat_number?: string | null;
  vat_evidence_state?: string | null;
  notes: string | null;
  has_thumb: boolean;
  syncState: SyncState;
  failReason: string | null;
  updatedAt: string;
  extraction_error?: string | null;
  extraction_cost_usd?: number | null;
  money_reconcile_ok?: boolean | null;
  money_reconcile_note?: string | null;
  money_reconcile_overridden?: boolean | null;
  low_confidence_fields?: string[];
  fields_edited?: string[];
  confidence?: Record<string, string> | null;
  rechargeable?: boolean | null;
  client_id?: string | null;
  site_id?: string | null;
  recharge_job_id?: string | null;
  recharge_frozen?: boolean;
  xero_bill_id?: string | null;
  xero_attachment_status?: string | null;
  xero_push_error?: string | null;
  xero_push_outcome?: string | null;
  xero_invoice_number?: string | null;
  xero_contact_id?: string | null;
  xero_contact_name?: string | null;
  xero_bill_status?: string | null;
  xero_amount_due_pence?: number | null;
  xero_has_attachments?: boolean | null;
  xero_total_mismatch?: boolean;
  xero_contact_map_mismatch?: boolean;
  xero_date_mismatch?: boolean;
  xero_bill_date?: string | null;
  receipt_date_weekday?: string | null;
  receipt_date_month_named?: boolean | null;
  receipt_date_facts_known?: boolean;
  accept_receipt_date?: boolean;
  archived_at?: string | null;
  recharge_decision?: string | null;
  manual_resolved_at?: string | null;
  manual_resolve_reason?: string | null;
  manual_resolve_note?: string | null;
  /** set = waiting to write the mark. clear = waiting to remove it. */
  manual_resolve_pending?: 'set' | 'clear' | null;
  field_sources?: Record<string, string>;
  held?: boolean;
  held_reason?: string | null;
};

/** Still needs a local photo to reach the server (never confirmed upload). */
export function receiptNeedsLocalPhoto(item: LocalReceipt): boolean {
  return item.status === 'pending_upload';
}

/** Blob absent or empty while upload still required — data loss. */
export function receiptPhotoLost(item: LocalReceipt): boolean {
  if (!receiptNeedsLocalPhoto(item)) return false;
  return !item.blob || item.blob.size === 0;
}

interface ReceiptsDB extends DBSchema {
  receipts: {
    key: string;
    value: LocalReceipt;
    indexes: { by_sync: SyncState; by_captured: string };
  };
}

let dbPromise: Promise<IDBPDatabase<ReceiptsDB>> | null = null;

export function getReceiptsDb() {
  if (!dbPromise) {
    dbPromise = openDB<ReceiptsDB>('terratech-receipts', 1, {
      upgrade(db) {
        const store = db.createObjectStore('receipts', { keyPath: 'id' });
        store.createIndex('by_sync', 'syncState');
        store.createIndex('by_captured', 'captured_at');
      },
    });
  }
  return dbPromise;
}

export async function putReceiptLocal(item: LocalReceipt): Promise<void> {
  const db = await getReceiptsDb();
  await db.put('receipts', item);
}

export async function getReceiptLocal(
  id: string,
): Promise<LocalReceipt | undefined> {
  const db = await getReceiptsDb();
  return db.get('receipts', id);
}

export async function listReceiptsLocal(): Promise<LocalReceipt[]> {
  const db = await getReceiptsDb();
  const all = await db.getAll('receipts');
  return all.sort((a, b) => b.captured_at.localeCompare(a.captured_at));
}

export async function listReceiptsBySync(
  state: SyncState,
): Promise<LocalReceipt[]> {
  const db = await getReceiptsDb();
  return db.getAllFromIndex('receipts', 'by_sync', state);
}

export async function countReceiptsPending(): Promise<number> {
  const pending = await listReceiptsBySync('pending');
  const syncing = await listReceiptsBySync('syncing');
  return pending.length + syncing.length;
}

export async function deleteReceiptLocal(id: string): Promise<void> {
  const db = await getReceiptsDb();
  const row = await db.get('receipts', id);
  if (row?.localPreviewUrl) {
    try {
      URL.revokeObjectURL(row.localPreviewUrl);
    } catch (err) {
      console.debug('revokeObjectURL failed', err);
    }
  }
  await db.delete('receipts', id);
}

/**
 * Replace a lost/failed photo on an existing receipt and requeue upload.
 * Does not release the previous blob URL until the new one is stored.
 */
export async function retakeReceiptLocal(opts: {
  id: string;
  blob: Blob;
  mime_type: string;
}): Promise<LocalReceipt | undefined> {
  if (opts.blob.size > MAX_RECEIPT_BYTES) {
    throw new ReceiptTooLargeError(opts.blob.size);
  }
  const cur = await getReceiptLocal(opts.id);
  if (!cur) return undefined;
  if (cur.localPreviewUrl) {
    try {
      URL.revokeObjectURL(cur.localPreviewUrl);
    } catch (err) {
      console.debug('revokeObjectURL failed', err);
    }
  }
  const preview = URL.createObjectURL(opts.blob);
  const next: LocalReceipt = {
    ...cur,
    blob: opts.blob,
    mime_type: opts.mime_type,
    localPreviewUrl: preview,
    status: 'pending_upload',
    syncState: 'pending',
    failReason: null,
    has_thumb: false,
    updatedAt: new Date().toISOString(),
  };
  await putReceiptLocal(next);
  return next;
}

export async function mergeReceiptsFromServer(
  remote: Array<{
    id: string;
    status: string;
    captured_at: string;
    mime_type: string;
    supplier_name: string | null;
    receipt_date: string | null;
    currency?: string | null;
    net_pence: number | null;
    vat_pence: number | null;
    gross_pence: number | null;
    gbp_net_pence?: number | null;
    gbp_vat_pence?: number | null;
    gbp_gross_pence?: number | null;
    fx_rate?: number | null;
    fx_rate_as_of?: string | null;
    fx_source?: string | null;
    fx_period?: string | null;
    category: string | null;
    supplier_vat_number?: string | null;
    vat_evidence_state?: string | null;
    notes: string | null;
    has_thumb?: boolean;
    extraction_error?: string | null;
    extraction_cost_usd?: number | null;
    money_reconcile_ok?: boolean | null;
    money_reconcile_note?: string | null;
    money_reconcile_overridden?: boolean | null;
    low_confidence_fields?: string[];
    fields_edited?: string[];
    extraction_suggested?: {
      confidence?: Record<string, string>;
    } | null;
    rechargeable?: boolean | null;
    client_id?: string | null;
    site_id?: string | null;
    recharge_job_id?: string | null;
    recharge_frozen?: boolean;
    xero_bill_id?: string | null;
    xero_attachment_status?: string | null;
    xero_push_error?: string | null;
    xero_push_outcome?: string | null;
    xero_invoice_number?: string | null;
    xero_contact_id?: string | null;
    xero_contact_name?: string | null;
    xero_bill_status?: string | null;
    xero_amount_due_pence?: number | null;
    xero_has_attachments?: boolean | null;
    xero_total_mismatch?: boolean;
    xero_contact_map_mismatch?: boolean;
    xero_date_mismatch?: boolean;
    xero_bill_date?: string | null;
    receipt_date_weekday?: string | null;
    receipt_date_month_named?: boolean | null;
    receipt_date_facts_known?: boolean;
    archived_at?: string | null;
    recharge_decision?: string | null;
    manual_resolved_at?: string | null;
    manual_resolve_reason?: string | null;
    manual_resolve_note?: string | null;
    field_sources?: Record<string, string>;
    held?: boolean;
    held_reason?: string | null;
  }>,
): Promise<void> {
  const db = await getReceiptsDb();
  const local = await db.getAll('receipts');
  const localById = new Map(local.map((r) => [r.id, r]));

  for (const r of remote) {
    const prev = localById.get(r.id);
    // Never clobber an in-flight or failed local upload — that was discarding
    // fail state (and could race with a still-held blob).
    if (
      prev &&
      (prev.manual_resolve_pending ||
        prev.syncState === 'pending' ||
        prev.syncState === 'syncing' ||
        prev.syncState === 'failed')
    ) {
      continue;
    }
    const next: LocalReceipt = {
      id: r.id,
      status: r.status as ReceiptStatus,
      captured_at: r.captured_at,
      mime_type: r.mime_type,
      // Server-confirmed rows do not need the local blob.
      blob: null,
      localPreviewUrl: prev?.localPreviewUrl ?? null,
      supplier_name: r.supplier_name,
      receipt_date: r.receipt_date,
      currency: r.currency ?? prev?.currency ?? null,
      net_pence: r.net_pence,
      vat_pence: r.vat_pence,
      gross_pence: r.gross_pence,
      gbp_net_pence: r.gbp_net_pence ?? null,
      gbp_vat_pence: r.gbp_vat_pence ?? null,
      gbp_gross_pence: r.gbp_gross_pence ?? null,
      fx_rate: r.fx_rate ?? null,
      fx_rate_as_of: r.fx_rate_as_of ?? null,
      fx_source: r.fx_source ?? null,
      fx_period: r.fx_period ?? null,
      category: r.category,
      supplier_vat_number: r.supplier_vat_number ?? null,
      vat_evidence_state: r.vat_evidence_state ?? null,
      notes: r.notes,
      has_thumb: Boolean(r.has_thumb),
      syncState: 'synced',
      failReason: null,
      updatedAt: new Date().toISOString(),
      extraction_error: r.extraction_error ?? null,
      extraction_cost_usd: r.extraction_cost_usd ?? null,
      money_reconcile_ok: r.money_reconcile_ok ?? null,
      money_reconcile_note: r.money_reconcile_note ?? null,
      money_reconcile_overridden: r.money_reconcile_overridden ?? false,
      low_confidence_fields: r.low_confidence_fields ?? [],
      fields_edited: r.fields_edited ?? [],
      confidence: r.extraction_suggested?.confidence ?? null,
      rechargeable: r.rechargeable ?? null,
      client_id: r.client_id ?? null,
      site_id: r.site_id ?? null,
      recharge_job_id: r.recharge_job_id ?? null,
      recharge_frozen: r.recharge_frozen ?? false,
      xero_bill_id: r.xero_bill_id ?? null,
      xero_attachment_status: r.xero_attachment_status ?? null,
      xero_push_error: r.xero_push_error ?? null,
      xero_push_outcome: r.xero_push_outcome ?? null,
      xero_invoice_number: r.xero_invoice_number ?? null,
      xero_contact_id: r.xero_contact_id ?? null,
      xero_contact_name: r.xero_contact_name ?? null,
      xero_bill_status: r.xero_bill_status ?? null,
      xero_amount_due_pence: r.xero_amount_due_pence ?? null,
      xero_has_attachments: r.xero_has_attachments ?? null,
      xero_total_mismatch: r.xero_total_mismatch ?? false,
      xero_contact_map_mismatch: r.xero_contact_map_mismatch ?? false,
      xero_date_mismatch: r.xero_date_mismatch ?? false,
      xero_bill_date: r.xero_bill_date ?? null,
      receipt_date_weekday: r.receipt_date_weekday ?? null,
      receipt_date_month_named:
        r.receipt_date_month_named === undefined
          ? null
          : r.receipt_date_month_named,
      receipt_date_facts_known: Boolean(r.receipt_date_facts_known),
      archived_at: r.archived_at ?? null,
      recharge_decision: r.recharge_decision ?? null,
      manual_resolved_at: r.manual_resolved_at ?? null,
      manual_resolve_reason: r.manual_resolve_reason ?? null,
      manual_resolve_note: r.manual_resolve_note ?? null,
      manual_resolve_pending: null,
      field_sources: r.field_sources ?? {},
      held: Boolean(r.held),
      held_reason: r.held_reason ?? null,
    };
    if (prev?.localPreviewUrl && prev.blob) {
      try {
        URL.revokeObjectURL(prev.localPreviewUrl);
      } catch (err) {
        console.debug('revokeObjectURL failed', err);
      }
      next.localPreviewUrl = null;
    }
    await db.put('receipts', next);
  }
}

/** Capture path: store blob locally first (must succeed offline). */
export async function captureReceiptLocal(opts: {
  id: string;
  blob: Blob;
  mime_type: string;
}): Promise<LocalReceipt> {
  if (opts.blob.size > MAX_RECEIPT_BYTES) {
    throw new ReceiptTooLargeError(opts.blob.size);
  }
  const preview = URL.createObjectURL(opts.blob);
  const item: LocalReceipt = {
    id: opts.id,
    status: 'pending_upload',
    captured_at: new Date().toISOString(),
    mime_type: opts.mime_type,
    blob: opts.blob,
    localPreviewUrl: preview,
    supplier_name: null,
    // Default to today so Confirm can land in month totals without re-typing.
    receipt_date: new Date().toISOString().slice(0, 10),
    currency: null,
    net_pence: null,
    vat_pence: null,
    gross_pence: null,
    category: null,
    supplier_vat_number: null,
    vat_evidence_state: null,
    notes: null,
    has_thumb: false,
    syncState: 'pending',
    failReason: null,
    updatedAt: new Date().toISOString(),
  };
  await putReceiptLocal(item);
  return item;
}
